import Flex from '@Components/Bases/Flex';
import FormCheckbox from '@Components/Form/FormCheckBox';
import {
  checkAssetsValidEn,
  checkAssetsValidTh,
} from '@Containers/UploadImageFormFields/utils';
import { defaultCompletedSection } from '@Domains/utils';
import { Divider, Typography } from '@mui/material';
import { formatToShortThaiDateTime } from '@Utils/datetime';
import { resetCompletedSection } from '@Utils/updateCompletedSection';
import { useFormContext, useWatch } from 'react-hook-form';
import { useProgressBar } from 'src/Constant/ProgressBarsContext/ProgressBar.Context';
import { useGreenhouseForm } from '../EsgGreenhouseFormProvider';
import { IPerformanceGhGasMgmtReportFormValues } from '../EsgGreenhouseFormProvider/types';
import {
  checkGhGasEmissionVerifyEn,
  checkGhGasEmissionVerifyRemarkSpaceValid,
  checkGhGasEmissionVerifySpaceValid,
  checkGhGasEmissionVerifyTh,
  checkGhGasEmissionVolumeRemarkEnValid,
  checkGhGasEmissionVolumeRemarkSpaceValid,
  checkPerfGhGasMgmtIntroSpaceValid,
  checkPerfGhGasMgmtRemarkEnValid,
  checkPerfGhGasMgmtReportIntroRemarkEnValid,
  checkPerfGhGasMgmtReportIntroSpaceValid,
  checkPerfGhGasMgmtReportRemarkSpaceValid,
  checkPerformanceGhGasMgmtEn,
  checkPerformanceGhGasMgmtTh,
} from './utils';
import { checkValidRemarkEn } from '@Utils/validationIntroRemark';

const GreenhouseGasMgmtReportCompletedCheckbox = () => {
  const { watch } = useGreenhouseForm();
  const { isPublished } = useProgressBar();
  const performanceGhGasMgmtReport = watch(
    'performanceGhGasMgmtReport'
  ) as IPerformanceGhGasMgmtReportFormValues;
  const { setValue } = useFormContext();

  const { performanceGhGasMgmt, ghGasEmissionVerify, ghGasEmissionVolume } =
    performanceGhGasMgmtReport || {};

  const completedSection =
    performanceGhGasMgmtReport?.completedSection || defaultCompletedSection;

  const { dateTh, dateEn, roleStampTh, roleStampEn } = completedSection;

  const formattedCompletedSectionDateTh = dateTh
    ? formatToShortThaiDateTime(dateTh)
    : '';
  const formattedCompletedSectionDateEn = dateEn
    ? formatToShortThaiDateTime(dateEn)
    : '';

  const isCompletedTh = useWatch({
    name: 'performanceGhGasMgmtReport.completedSection.isCompletedTh',
  });
  const isCompletedEn = useWatch({
    name: 'performanceGhGasMgmtReport.completedSection.isCompletedEn',
  });

  const isCompletedThAll =
    checkPerformanceGhGasMgmtTh(performanceGhGasMgmt) &&
    ghGasEmissionVerify !== undefined &&
    checkGhGasEmissionVerifyTh(ghGasEmissionVerify) &&
    checkAssetsValidTh(performanceGhGasMgmt) &&
    checkPerfGhGasMgmtReportIntroSpaceValid(performanceGhGasMgmtReport, 'Th') &&
    checkPerfGhGasMgmtReportRemarkSpaceValid(
      performanceGhGasMgmtReport,
      'Th'
    ) &&
    checkPerfGhGasMgmtIntroSpaceValid(performanceGhGasMgmt, 'Th') &&
    checkGhGasEmissionVolumeRemarkSpaceValid(ghGasEmissionVolume, 'Th') &&
    checkGhGasEmissionVerifySpaceValid(ghGasEmissionVerify, 'Th') &&
    checkGhGasEmissionVerifyRemarkSpaceValid(ghGasEmissionVerify, 'Th');

  const isCompletedEnAll =
    checkPerformanceGhGasMgmtEn(performanceGhGasMgmt) &&
    ghGasEmissionVerify !== undefined &&
    checkPerfGhGasMgmtReportIntroRemarkEnValid(performanceGhGasMgmtReport) &&
    checkPerfGhGasMgmtRemarkEnValid(performanceGhGasMgmt) &&
    checkGhGasEmissionVerifyEn(ghGasEmissionVerify) &&
    checkAssetsValidEn(performanceGhGasMgmt) &&
    checkGhGasEmissionVolumeRemarkEnValid(ghGasEmissionVolume) &&
    checkPerfGhGasMgmtReportIntroSpaceValid(performanceGhGasMgmtReport, 'En') &&
    checkPerfGhGasMgmtReportRemarkSpaceValid(
      performanceGhGasMgmtReport,
      'En'
    ) &&
    checkPerfGhGasMgmtIntroSpaceValid(performanceGhGasMgmt, 'En') &&
    checkGhGasEmissionVolumeRemarkSpaceValid(ghGasEmissionVolume, 'En') &&
    checkGhGasEmissionVerifySpaceValid(ghGasEmissionVerify, 'En') &&
    checkGhGasEmissionVerifyRemarkSpaceValid(ghGasEmissionVerify, 'En') &&
    checkValidRemarkEn(ghGasEmissionVerify?.remark);

  const handleCheckboxChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    language: 'Th' | 'En'
  ) => {
    const updatedSection = { ...completedSection };

    if (language === 'Th') {
      updatedSection.isCompletedTh = e.target.checked;
    } else {
      updatedSection.isCompletedEn = e.target.checked;
    }

    resetCompletedSection(
      updatedSection,
      setValue,
      'performanceGhGasMgmtReport'
    );
  };

  return (
    <>
      <Divider />
      <Flex gap={4} justifyContent={'center'} bgcolor={'grey.50'} py={1}>
        <Flex alignItems={'center'}>
          <Typography fontWeight={800} textAlign={'right'}>
            ยืนยันการตรวจสอบข้อมูลครบถ้วน
          </Typography>
        </Flex>
        <Flex>
          <Flex flexDirection="column" alignItems={'center'}>
            <FormCheckbox
              name="performanceGhGasMgmtReport.completedSection.isCompletedTh"
              label="จัดเตรียมข้อมูลเรียบร้อย (ภาษาไทย)"
              inputProps={{ disabled: isPublished || !isCompletedThAll }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleCheckboxChange(e, 'Th')
              }
            />
            {isCompletedTh && dateTh && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ display: 'block' }}
              >
                โดย {roleStampTh} {formattedCompletedSectionDateTh}
              </Typography>
            )}
          </Flex>
          <Flex flexDirection="column" alignItems={'center'}>
            <FormCheckbox
              sx={{ ml: 2 }}
              name="performanceGhGasMgmtReport.completedSection.isCompletedEn"
              label="จัดเตรียมข้อมูลเรียบร้อย (ภาษาอังกฤษ)"
              inputProps={{ disabled: isPublished || !isCompletedEnAll }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleCheckboxChange(e, 'En')
              }
            />
            {isCompletedEn && dateEn && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ display: 'block' }}
              >
                โดย {roleStampEn} {formattedCompletedSectionDateEn}
              </Typography>
            )}
          </Flex>
        </Flex>
      </Flex>
    </>
  );
};

export default GreenhouseGasMgmtReportCompletedCheckbox;
